import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, Row, Col, Avatar, Typography, Space, Divider, message } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import * as adminactions from 'store/actions/AuthActions';
import { refreshUserData } from 'store/slices/AuthSlice';

const { Title, Text } = Typography;

const UserInformation = ({ isProfileLocked = false, employee = null }) => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const user = useSelector(state => state.auth.user);
  const dispatch = useDispatch();

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username,
        email: user.email,
        mobile: user.mobile || '',
      });
    }
  }, [user, form]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const { username, ...updateData } = values;
      const response = await adminactions.updateUsers(updateData, user.id);
      if (response && !response.message && !response.response) {
        message.success('Profile updated successfully!');
        dispatch(refreshUserData());
        setIsEditing(false);
      } else {
        const errData = response?.response?.data;
        if (errData && typeof errData === 'object') {
          const firstError = Object.entries(errData).map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`).join('; ');
          message.error(firstError || 'Failed to update profile');
        } else {
          message.error(response?.message || 'Failed to update profile');
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    if (!isProfileLocked) {
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    form.resetFields();
  };

  return (
    <div style={{ padding: '20px' }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Avatar size={120} icon={<UserOutlined />} style={{ marginBottom: '16px' }} />
              <Title level={4}>{user?.first_name} {user?.last_name}</Title>
              <Text type="secondary">{user?.username}</Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={16} md={18}>
          <Card title="Personal Information"
                extra={
                  !isEditing && !isProfileLocked ? (
                    <Button type="primary" onClick={handleEdit}>
                      Edit
                    </Button>
                  ) : isProfileLocked ? (
                    <Button disabled type="primary">
                      Profile Locked
                    </Button>
                  ) : null
                }>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              disabled={!isEditing || isProfileLocked}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="First Name"
                    name="first_name"
                    rules={[{ required: true, message: 'Please input your first name!' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="First Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Last Name"
                    name="last_name"
                    rules={[{ required: true, message: 'Please input your last name!' }]}
                  >
                    <Input prefix={<UserOutlined />} placeholder="Last Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Username"
                    name="username"
                  >
                    <Input prefix={<UserOutlined />} placeholder="Username" disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Please input your email!' },
                      { type: 'email', message: 'Please enter a valid email!' }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Email" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Mobile"
                    name="mobile"
                  >
                    <Input prefix={<PhoneOutlined />} placeholder="Mobile Number" />
                  </Form.Item>
                </Col>
              </Row>

              {isEditing && !isProfileLocked && (
                <>
                  <Divider />
                  <Space>
                    <Button type="primary" htmlType="submit" loading={saving}>
                      Save Changes
                    </Button>
                    <Button onClick={handleCancel}>
                      Cancel
                    </Button>
                  </Space>
                </>
              )}
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default UserInformation;
